import { useMemo, useState } from 'react';
import { Download } from 'lucide-react';
import { useFormSubmissions, useSubmissionTypes } from '@/api/hooks';
import { api } from '@/api/client';

type Row = Record<string, unknown>;
interface Grouped {
  id: number;
  formType: string;
  by: string;
  email: string;
  date: string;
  notes: string;
  emailed: boolean;
  details: { clientId: string; clientName: string; engagementId: string; service: string; effectiveDate: string | null }[];
}

const fmt = (v: unknown) => (v ? new Date(String(v)).toLocaleString() : '');
const fmtDate = (v: unknown) => (v ? String(v).slice(0, 10) : '');

export function RequestsList() {
  const [formType, setFormType] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const q = useFormSubmissions({ formType, q: search, from, to });
  const types = useSubmissionTypes();

  const groups = useMemo<Grouped[]>(() => {
    const map = new Map<number, Grouped>();
    for (const r of (q.data ?? []) as Row[]) {
      const id = Number(r.SubmissionID);
      let g = map.get(id);
      if (!g) {
        g = {
          id,
          formType: String(r.FormType ?? ''),
          by: String(r.SubmittedBy ?? ''),
          email: String(r.SubmittedByEmail ?? ''),
          date: fmt(r.SubmittedDateUtc),
          notes: String(r.Notes ?? ''),
          emailed: !!r.AdminEmailedUtc,
          details: [],
        };
        map.set(id, g);
      }
      if (r.ClientID || r.EngagementID) {
        g.details.push({
          clientId: String(r.ClientID ?? ''),
          clientName: String(r.ClientName ?? ''),
          engagementId: String(r.EngagementID ?? ''),
          service: String(r.Service ?? ''),
          effectiveDate: r.EffectiveDate ? fmtDate(r.EffectiveDate) : null,
        });
      }
    }
    return [...map.values()];
  }, [q.data]);

  const hasFilter = formType || search || from || to;

  function exportXlsx() {
    const qs = new URLSearchParams();
    if (formType) qs.set('formType', formType);
    if (search) qs.set('q', search);
    if (from) qs.set('from', from);
    if (to) qs.set('to', to);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    api.download(`/forms/submissions.xlsx${suffix}`, 'submitted-requests.xlsx');
  }

  return (
    <div className="flex h-full flex-col gap-4 p-6">
      <div className="flex flex-shrink-0 flex-wrap items-end gap-3">
        <div>
          <label className="label">Form Type</label>
          <select className="input w-44" value={formType} onChange={(e) => setFormType(e.target.value)}>
            <option value="">All</option>
            {(types.data ?? []).map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="min-w-[220px] flex-1">
          <label className="label">Search (client or submitter)</label>
          <div className="flex gap-2">
            <input className="input" placeholder="Client name / ID or submitter…" value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && setSearch(searchInput)} />
            <button className="btn-secondary" onClick={() => setSearch(searchInput)}>Search</button>
          </div>
        </div>
        <div>
          <label className="label">Submitted from</label>
          <input type="date" className="input w-40" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div>
          <label className="label">to</label>
          <input type="date" className="input w-40" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        {hasFilter && (
          <button className="btn-secondary" onClick={() => { setFormType(''); setSearch(''); setSearchInput(''); setFrom(''); setTo(''); }}>
            Clear
          </button>
        )}
        <button className="btn-secondary ml-auto flex items-center gap-2" onClick={exportXlsx} disabled={groups.length === 0}>
          <Download className="h-4 w-4" /> Export
        </button>
      </div>

      {q.isFetching && <p className="text-sm text-muted-foreground">Loading…</p>}
      {!q.isFetching && groups.length === 0 && <p className="text-sm text-muted-foreground">No requests match.</p>}
      <div className="min-h-0 flex-1 space-y-3 overflow-auto">
        {groups.map((g) => (
          <div key={g.id} className="card">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <div className="text-sm font-semibold text-primary">#{g.id} · {g.formType}</div>
              <div className="text-xs text-muted-foreground">{g.date} · {g.by} ({g.email}) {g.emailed ? '· admins notified' : '· email pending'}</div>
            </div>
            {(() => {
              const hasEng = g.details.some((d) => d.engagementId);
              return (
                <table className="mt-2 w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-muted-foreground">
                      <th className="py-1 pr-4">Client ID</th>
                      <th className="py-1 pr-4">Client Name</th>
                      {hasEng && <th className="py-1 pr-4">Engagement</th>}
                      {hasEng && <th className="py-1 pr-4">Service</th>}
                      <th className="py-1">Effective Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.details.map((d, i) => (
                      <tr key={i} className="border-t border-border/60">
                        <td className="py-1 pr-4">{d.clientId}</td>
                        <td className="py-1 pr-4">{d.clientName}</td>
                        {hasEng && <td className="py-1 pr-4">{d.engagementId}</td>}
                        {hasEng && <td className="py-1 pr-4">{d.service}</td>}
                        <td className="py-1">{d.effectiveDate ?? ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              );
            })()}
            {g.notes && <p className="mt-2 text-xs text-muted-foreground"><b>Notes:</b> {g.notes}</p>}
          </div>
        ))}
      </div>
      <p className="flex-shrink-0 text-xs text-muted-foreground">Showing up to 500 most-recent submissions. Narrow with the filters above.</p>
    </div>
  );
}
